import React, { useMemo, useCallback } from "react";

import { REVIEW_MODAL_SUBMIT_FEEDBACK } from "~/analytics/Amplitude/amplitudeConstants";
import { addUTMPropertiesToEvent } from "~/analytics/Amplitude/amplitudeUtils";
import { useAnalytics } from "~/analytics/useAnalytics";
import { addProductReview, getProductReviews } from "~/api/reviewsApi";
import { useAuth } from "~/hooks/auth/useAuth";

interface ReviewProps {
  children: React.ReactNode;
}

export type ReviewSubmitArgs = {
  productId: string;
  userId: string | undefined;
  reviewerName: string;
  reviewerProfileImageURL: string;
  reviewText: string;
  reviewScore: number;
  source: string;
  productName: string;
  creatorName: string;
};

export interface Review {
  createdAt: string;
  id: string;
  isFeatured: boolean;
  isHidden: boolean;
  isValid: boolean;
  lastUpdatedAt: string;
  productId: string;
  rankIndex: number;
  reviewScore: number;
  reviewText: string;
  reviewerName: string;
  reviewerProfileImageURL: string;
  source: string;
  userId: string;
}

interface ReviewState {
  isLoading: boolean;
  currentReview: Review | null | undefined;
  updateReviewScore({ productId, reviewScore }: { productId: string; reviewScore: number }): void;
  getReview(productId: string): Promise<any>;
  submitReview(args: ReviewSubmitArgs): Promise<any>;
}

export const ReviewContext = React.createContext<ReviewState>({
  isLoading: false,
  currentReview: null,
  updateReviewScore: () => {},
  getReview: async () => ({ type: "error", error: new Error("not initialized") }),
  submitReview: async () => ({ type: "error", error: new Error("not initialized") }),
});

export const ReviewProvider = ({ children }: ReviewProps) => {
  const { user } = useAuth();
  const { trackAmplitudeEvent } = useAnalytics();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [currentReview, setCurrentReview] = React.useState<Review | undefined>();

  const getReview = useCallback(
    async (productId: string) => {
      const result = await getProductReviews(productId);
      const userReview = result.find((review) => review.userId === user?.userId);
      setCurrentReview(userReview);
    },
    [user?.userId],
  );

  const submitReview = useCallback(
    async (args: ReviewSubmitArgs) => {
      setIsLoading(true);
      await addProductReview({
        ...args,
        ...(currentReview?.productId && { productId: currentReview.productId }),
      }).then(() => {
        getReview(currentReview?.productId ? currentReview.productId : args.productId);
        trackAmplitudeEvent(REVIEW_MODAL_SUBMIT_FEEDBACK, {
          ...addUTMPropertiesToEvent(),
          reviewScore: args.reviewScore,
          reviewText: args.reviewText,
          productName: args.productName,
          creatorName: args.creatorName,
        });
        setCurrentReview(undefined);
        setIsLoading(false);
      });
    },
    [getReview, currentReview, trackAmplitudeEvent],
  );

  const updateReviewScore = useCallback(
    async (data: { productId: string; reviewScore: number }) => {
      setCurrentReview({
        createdAt: "",
        id: "",
        isFeatured: false,
        isHidden: false,
        isValid: false,
        lastUpdatedAt: "",
        productId: data.productId,
        rankIndex: 0,
        reviewScore: data.reviewScore,
        reviewText: "",
        reviewerName: "",
        reviewerProfileImageURL: "",
        source: "",
        userId: "",
      });
    },
    [setCurrentReview],
  );

  const contextValue = useMemo(
    () => ({ submitReview, getReview, currentReview, isLoading, updateReviewScore }),
    [currentReview, getReview, submitReview, isLoading, updateReviewScore],
  );

  return <ReviewContext.Provider value={contextValue}>{children}</ReviewContext.Provider>;
};
